"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import appSettings from "@/shared/config/appSettings";
import {
  useSearchQueryBacklog,
  useSearchQueryCheck,
  useSearchQuerySelect,
  useUserChoices,
  useBacklogSearchControls,
  useCheckSearchControls,
  useSelectSearchControls,
} from "@/state/globalStore";
import { mindstringPrototypesSlang } from "@/shared/config/backend";
import {
  MEILI_SEARCH_CONFIG,
  getDefaultSearchSortValue,
} from "../config/meiliSearchConfig";
import {
  SEARCH_PAGE_META,
  type SearchPageView,
  resolveSearchPageView,
} from "../config/searchPageConfig";

const DEFAULT_SEARCH_SEMANTIC_RATIO = 0.5;

export default function usePageSearch() {
  const pathname = usePathname();
  const {
    activeBacklogFocus,
    activePrototypeCategory,
    activeStudioContent,
    activeStudioExportLock,
  } = useUserChoices();

  const pageViewType = useMemo<SearchPageView>(
    () =>
      resolveSearchPageView({
        pathname,
        activeStudioContent,
        activeStudioExportLock,
      }),
    [pathname, activeStudioContent, activeStudioExportLock],
  );
  const pageMeta = SEARCH_PAGE_META[pageViewType];

  const backlogSearch = useSearchQueryBacklog();
  const selectSearch = useSearchQuerySelect();
  const checkSearch = useSearchQueryCheck();

  const backlogControls = useBacklogSearchControls();
  const selectControls = useSelectSearchControls();
  const checkControls = useCheckSearchControls();

  const pageSearchByKey = {
    backlog: backlogSearch,
    check: checkSearch,
    select: selectSearch,
  } as const;
  const searchControlsByKey = {
    backlog: backlogControls,
    check: checkControls,
    select: selectControls,
  } as const;

  const pageSearch = pageSearchByKey[pageMeta.searchStateKey];
  const searchControls = searchControlsByKey[pageMeta.searchStateKey];
  const relevantSearchConfig = MEILI_SEARCH_CONFIG[pageMeta.configKey];
  const defaultSearchSortBy = getDefaultSearchSortValue(relevantSearchConfig);
  const defaultSearchHitsPerPage = appSettings.search.HITS_PER_PAGE;

  const hasActiveQuery = pageSearch.searchQuery.trim().length > 0;
  const hasLanguageFilters =
    !!searchControls.filters.language &&
    searchControls.filters.language.length > 0;
  const hasNonDefaultSearchAdjustments = useMemo(() => {
    const selectedLanguages = searchControls.filters.language ?? [];

    return (
      searchControls.hitsPerPage !== defaultSearchHitsPerPage ||
      searchControls.sortBy !== defaultSearchSortBy ||
      searchControls.semanticRatio !== DEFAULT_SEARCH_SEMANTIC_RATIO ||
      selectedLanguages.length > 0
    );
  }, [
    defaultSearchHitsPerPage,
    defaultSearchSortBy,
    searchControls.filters.language,
    searchControls.hitsPerPage,
    searchControls.semanticRatio,
    searchControls.sortBy,
  ]);

  const isAllowedPage = useMemo(() => {
    if (pathname?.includes("/studio")) {
      return true;
    }
    if (pathname?.includes("/backlog")) {
      return activeBacklogFocus === "extract";
    }
    return false;
  }, [pathname, activeBacklogFocus]);

  function resetPageSearch() {
    searchControls.resetSearchAdjustments();
    pageSearch.resetSearchQuery();
  }

  const breadcrumbs = useMemo(
    () => ({
      page: pathname?.includes("/admin") ? "admin" : pageMeta.breadcrumbPage,
      perspective:
        pageMeta.showPerspectiveBreadcrumb && activePrototypeCategory
          ? mindstringPrototypesSlang[activePrototypeCategory].label
          : undefined,
    }),
    [pathname, pageMeta, activePrototypeCategory],
  );

  const searchScopeLabel = useMemo(() => {
    switch (pageViewType) {
      case "backlog_extract":
        return "backlog archive";
      case "studio_check":
        return `experience suggestions`;
      case "studio_select":
      default:
        return `confirmed profile`;
    }
  }, [pageViewType]);

  function resolvePathname(pagePath: string): string {
    return pagePath === "home" ? "/" : `/${pagePath}`;
  }

  return {
    pageViewType,
    pageSearch,
    hasActiveQuery,
    hasLanguageFilters,
    hasNonDefaultSearchAdjustments,
    relevantSearchConfig,
    searchControls,
    showPerspectiveBreadcrumb: pageMeta.showPerspectiveBreadcrumb,
    showTargetFocusSection: pageMeta.showTargetFocusSection,
    useTallDialog: pageMeta.useTallDialog,
    isAllowedPage,
    resetPageSearch,
    breadcrumbs,
    searchScopeLabel,
    resolvePathname,
  };
}
